/**
 * WHAT THE FAMILY RECEIVES FROM THE GUARANTEED INCOME, IF DEATH COMES EARLY (owner, 21 Oct 2026 — the guaranteed
 * income review E4/M4/R2 and the agreed mock-up, docs/ux-review/annuities/ screens 3 and 4).
 *
 * Every "left to the family" figure assumes the person lives the whole planning period. A family whose mother
 * bought an income-only annuity and passed away months later was never shown that outcome. This panel shows it
 * on every plan with a guaranteed income, tested or hand-built, in every module: at the start, the middle and
 * the end of the planning period, what comes back from the guaranteed income, what the family receives in
 * total with this plan, and — where the module has it — the same plan without the guaranteed income.
 *
 * POSITIONING (owner: "we don't want to offend"). It sits inside the guaranteed-income panel as plain
 * information: "If Kerry passes away", no warning colour for the types that return the money; the amber rail
 * only for income only, where the table says "nothing".
 *
 * The figures are the projection's own: each year's row carries the assets as they stand, and an annuity's
 * value there is what it pays on death that year (annuityNetWorthValueOf: the full price for the
 * annuity-and-life-cover type, the capital-access schedule for money back, nothing for income only); "the
 * family receives" is that year's estate (net worth less any tax on selling an investment property, estateOf).
 */
import React from 'react';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { formatCurrency } from '../../utils/index.ts';
import { annuityNetWorthValueOf } from '../../../../shared/src/calculation/annuityStrategies.ts';
import { estateOf } from '../../../../shared/src/calculation/support/estateCgt.ts';

export type FamilyPanelKind = 'fullBack' | 'moneyBack' | 'incomeOnly';

interface Row { assets?: { annuities?: unknown[] } | Record<string, unknown>; netWorth?: number; estateCgt?: number | null }

export interface AnnuityFamilyPanelProps {
    factFind: DraftFactFind | null | undefined;
    /** Who the guaranteed income is for, as the screen names them ("Kerry"). */
    personName: string;
    /** The amount put into the guaranteed income. */
    amount: number;
    kind: FamilyPanelKind;
    /** About how much it pays a year, for the line under the table. */
    incomeAYear?: number | null;
    /** The plan's own yearly rows (projection). */
    rows: Row[] | null | undefined;
    /** The same plan without the guaranteed income, where the module has it. */
    compareRows?: Row[] | null;
    /** What the comparison is, in one sentence ("the same deposit, paid the same way, with the $150,000 left in savings"). */
    compareNote?: string | null;
    /** The years the plans are judged over. */
    horizonYears: number;
    testId?: string;
    /**
     * The table's figures frozen when a plan was chosen (a saved plan keeps no projection rows), as
     * buildSoaGuaranteedIncome makes them. When given, `rows` and `compareRows` are not read. Optional, additive.
     */
    frozen?: ReadonlyArray<{ label: string; fromAnnuity: number; withPlan: number | null; without: number | null }> | null;
}

/** The years to show: the first, the middle and the last of the planning period (distinct, within the rows). */
export function familyPanelYears(horizonYears: number, rowCount: number): number[] {
    const last = Math.max(1, Math.min(Math.round(horizonYears) || 1, rowCount || 1));
    const mid = Math.round((1 + last) / 2);
    return [...new Set([1, mid, last])].filter((y) => y >= 1 && y <= last);
}

/** Ids of the annuities already on the file, so the plan's new one can be told apart. */
function fileAnnuityIds(factFind: DraftFactFind | null | undefined): Set<string> {
    const list = ((factFind?.assets as { annuities?: Array<{ id?: unknown }> } | undefined)?.annuities) || [];
    return new Set(list.map((a) => String(a?.id)));
}

/** What the plan's own guaranteed income would pay the family on death in that year. */
export function annuityDeathValueAt(row: Row | undefined, factFind: DraftFactFind | null | undefined): number {
    const list = ((row?.assets as { annuities?: Array<{ id?: unknown }> } | undefined)?.annuities) || [];
    const onFile = fileAnnuityIds(factFind);
    return list.filter((a) => !onFile.has(String(a?.id))).reduce((t, a) => t + annuityNetWorthValueOf(a), 0);
}

const estate = (row: Row | undefined): number | null => (row ? estateOf(row as never) : null);

export const AnnuityFamilyPanel: React.FC<AnnuityFamilyPanelProps> = ({
    factFind, personName, amount, kind, incomeAYear, rows, compareRows, compareNote, horizonYears, testId = 'annuity-family-panel', frozen = null,
}) => {
    const list = rows || [];
    const frozenRows = frozen && frozen.length ? frozen : null;
    if ((!list.length && !frozenRows) || !(amount > 0)) return null;
    const years = familyPanelYears(horizonYears, list.length);
    const withCompare = frozenRows ? frozenRows.some((r) => r.without != null) : !!compareRows?.length;
    const when = (y: number) => (y === 1 ? 'In the first year' : `After ${y} years`);
    const money = (n: number | null) => (n == null ? '—' : formatCurrency(Math.round(n)));
    const amountWords = formatCurrency(Math.round(amount));
    const income = incomeAYear != null && incomeAYear > 0 ? `${personName} receives about ${formatCurrency(Math.round(incomeAYear))} a year for life. ` : '';
    const closing = kind === 'fullBack'
        ? `${income}The full ${amountWords} goes back to the family at any time. Most of what is paid buys life cover, so the pension and fee savings are smaller than with the other types.`
        : kind === 'moneyBack'
            ? `${income}The whole ${amountWords} goes back to the family in the early years; after that a smaller amount, falling to nothing once ${personName} reaches life expectancy.`
            : `${income}If ${personName} passes away early, the ${amountWords} is not returned, so the family receives less than without it.`;
    return (
        <div
            className={`space-y-2 rounded-xl border border-slate-200 border-l-4 bg-white px-4 py-3 text-[13px] text-slate-700 ${kind === 'incomeOnly' ? 'border-l-amber-400' : 'border-l-brand-navy'}`}
            data-testid={testId}
        >
            <p className="text-[14px] font-semibold text-slate-900">What the family receives from the guaranteed income</p>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[360px] text-[13px] tabular-nums">
                    <thead>
                        <tr className="border-b border-slate-200 text-left align-bottom text-[11.5px] font-semibold leading-tight text-slate-500">
                            <th className="py-1.5 pr-3 font-semibold">If {personName} passes away</th>
                            <th className="py-1.5 pr-3 text-right">Comes back</th>
                            <th className="py-1.5 pr-3 text-right">The family receives</th>
                            {withCompare && <th className="py-1.5 text-right">Without it</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {frozenRows && frozenRows.map((r, i) => (
                            <tr key={r.label} className="border-b border-slate-100 last:border-0" data-testid={`${testId}-row-${i}`}>
                                <td className="whitespace-nowrap py-1.5 pr-3">{r.label}</td>
                                <td className="py-1.5 pr-3 text-right">{r.fromAnnuity < 1 ? 'nothing' : money(r.fromAnnuity)}</td>
                                <td className="py-1.5 pr-3 text-right">{money(r.withPlan)}</td>
                                {withCompare && <td className="py-1.5 text-right">{money(r.without)}</td>}
                            </tr>
                        ))}
                        {!frozenRows && years.map((y) => {
                            const back = annuityDeathValueAt(list[y - 1], factFind);
                            return (
                                <tr key={y} className="border-b border-slate-100 last:border-0" data-testid={`${testId}-year-${y}`}>
                                    <td className="whitespace-nowrap py-1.5 pr-3">{when(y)}</td>
                                    <td className="py-1.5 pr-3 text-right">{back < 1 ? 'nothing' : money(back)}</td>
                                    <td className="py-1.5 pr-3 text-right">{money(estate(list[y - 1]))}</td>
                                    {withCompare && <td className="py-1.5 text-right">{money(estate(compareRows![Math.min(y, compareRows!.length) - 1]))}</td>}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {withCompare && compareNote && <p className="text-[12.5px] text-slate-500">“Without it” is {compareNote}, so the difference is the guaranteed income alone.</p>}
            <p className="text-[12.5px] text-slate-600">{closing}</p>
        </div>
    );
};

/** Which panel kind an annuity is, from its type and residual share. */
export function familyPanelKindOf(a: { annuityType?: unknown; residualCapital?: unknown; annuityResidualPercent?: unknown } | null | undefined): FamilyPanelKind {
    if (a?.annuityType === 'guaranteed') return 'fullBack';
    const r = a?.residualCapital != null ? Number(a.residualCapital) : Number(a?.annuityResidualPercent) / 100;
    return Number.isFinite(r) && r > 0 ? 'moneyBack' : 'incomeOnly';
}

export default AnnuityFamilyPanel;
